import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Typography } from '@mui/material';
import LegalPage, { LegalSection } from '../components/LegalPage';
import { LEGAL } from '../legal';
import { BRAND } from '../theme';

// Keep this in step with what the app actually does: every statement below
// describes the current code and infrastructure (see src/legal.ts).

const email = <Link href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</Link>;
const P: React.FC<{ children: React.ReactNode }> = ({ children }) => <Typography component="p">{children}</Typography>;

const sections: LegalSection[] = [
  {
    id: 'who-we-are',
    title: 'Who we are',
    body: (
      <>
        <P>
          {BRAND.name} is operated by {LEGAL.operatorName}, an individual based in {LEGAL.postalAddress} ("we",
          "us"). We are responsible
          for the personal data described in this policy (the "data fiduciary" under India's Digital Personal Data
          Protection Act, 2023, and the "controller" under the GDPR where it applies).
        </P>
        <P>For any privacy question or request, email {email}.</P>
      </>
    ),
  },
  {
    id: 'what-we-collect',
    title: 'Information we collect',
    body: (
      <>
        <P>We collect only what we need to run {BRAND.name}:</P>
        <ul>
          <li>
            <strong>Account details:</strong> your email address, your name (if you give one), your password (stored
            only as a one-way salted hash, never in readable form), whether your email address is verified, and when
            you joined and last signed in.
          </li>
          <li>
            <strong>Sign-in with Google or Facebook</strong> (only if you choose it): your name, email address,
            whether the provider has verified that email, and the provider's ID for your account. We do not receive
            your Google or Facebook password, we do not store the access tokens the provider issues, and we never
            post on your behalf or read your contacts, photos or other data.
          </li>
          <li>
            <strong>Content you create:</strong> tasks (title, description, due date, priority, status), categories,
            and time logs (task name, date, duration, description).
          </li>
          <li>
            <strong>AI assistant conversations:</strong> the messages you send to the assistant, its replies, and the
            changes it makes to your tasks, categories and time logs at your request.
          </li>
          <li>
            <strong>AI assistant usage:</strong> for each message to the assistant, when it was sent, how many AI
            tokens it used, its cost in credits and whether it was answered or blocked (never its content), plus your
            credit totals per week, month, year and overall. These are stored against a one-way keyed hash of your
            email address rather than your account, so usage limits can't be reset by deleting and re-creating an
            account (see section 7).
          </li>
          <li>
            <strong>Technical data:</strong> our servers log requests and errors, including your account email or
            ID, to keep the service secure and fix problems. Our hosting provider processes your IP address and basic
            request details to deliver the service.
          </li>
          <li>
            <strong>On your device:</strong> after you sign in, your browser keeps sign-in tokens in local storage so
            you stay signed in. We do not use advertising or analytics cookies or trackers.
          </li>
        </ul>
        <P>Please don't put sensitive information (such as health, financial or government ID details) in your tasks, time logs or chats.</P>
      </>
    ),
  },
  {
    id: 'how-we-use',
    title: 'How we use your information',
    body: (
      <>
        <ul>
          <li>to create and secure your account and sign you in;</li>
          <li>to store and show your tasks, categories, time logs and analytics;</li>
          <li>to run the AI assistant when you use it (see section 4);</li>
          <li>to prevent abuse, enforce usage limits, and investigate security incidents;</li>
          <li>to answer your requests and send essential service messages;</li>
          <li>to comply with the law.</li>
        </ul>
        <P>
          We process your data to provide the service you signed up for and on the basis of your consent, which you
          give by creating an account. We do <strong>not</strong> sell your personal data, show you ads, build
          marketing profiles, or use your content to train AI models.
        </P>
      </>
    ),
  },
  {
    id: 'ai-assistant',
    title: 'The AI assistant',
    body: (
      <>
        <P>
          When you message the assistant, your message, the conversation so far, and the task, category and time-log
          data it needs to answer are sent to <strong>Azure OpenAI Service</strong>, operated by Microsoft as our
          service provider. Microsoft processes this data under its agreement with us; under Microsoft's terms for
          Azure OpenAI Service it is not used to train Microsoft's or OpenAI's models. Microsoft may keep it for a
          limited time to monitor for abuse, as described in its product terms.
        </P>
        <P>
          We store your conversations so you can return to them. You can delete any conversation at any time in the
          app; this deletes its stored history from our database.
        </P>
        <P>
          The assistant has usage limits: each message uses credits based on its AI cost, and your plan allows a
          number of credits per week. The assistant is available once your email address is verified.
        </P>
      </>
    ),
  },
  {
    id: 'sharing',
    title: 'Who we share it with',
    body: (
      <>
        <P>We share personal data only with the service providers that run {BRAND.name} for us:</P>
        <ul>
          <li>
            <strong>Microsoft Azure</strong>: hosting, database and the AI assistant. Your data is stored in{' '}
            {LEGAL.hostingRegion}.
          </li>
          <li>
            <strong>Google and Meta (Facebook)</strong>: only if you use their sign-in. Their sign-in scripts load on
            our sign-in page, and what they collect is governed by their own privacy policies.
          </li>
          <li>
            <strong>Google Fonts</strong>: our pages load fonts from Google's servers, which receive your IP address
            and browser details as part of that request.
          </li>
        </ul>
        <P>
          {BRAND.name} is free today. If we introduce paid features (for example, the AI assistant), payments will be
          handled by a payment provider, and we will update this policy to describe it before any payment data is
          collected.
        </P>
        <P>
          We may also disclose data if the law requires it, to protect the rights and safety of our users or others,
          or to a successor if {BRAND.name} is sold or reorganised (it stays under this policy's protections).
        </P>
      </>
    ),
  },
  {
    id: 'google-user-data',
    title: 'Google user data',
    body: (
      <P>
        If you sign in with Google, we request only the <code>openid</code>, <code>email</code> and{' '}
        <code>profile</code> scopes. We use the data received (your name, email address, its verification status
        and your Google account ID) only to create your account, sign you in and show your profile. We do not share
        it with third parties except the service providers above, do not use it for advertising, and do not allow
        humans to read it except for security, legal compliance or with your permission. {BRAND.name}'s use of
        information received from Google APIs adheres to the{' '}
        <Link href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener">
          Google API Services User Data Policy
        </Link>
        , including the Limited Use requirements.
      </P>
    ),
  },
  {
    id: 'retention',
    title: 'How long we keep it',
    body: (
      <ul>
        <li>Account data and content: for as long as your account exists.</li>
        <li>AI conversations: until you delete them or your account.</li>
        <li>
          AI assistant usage records (per message): 30 days. Your credit totals: while your account exists, and for
          30 days after your last use of the assistant once your account is deleted.
        </li>
        <li>
          After you delete your account in the app: removed from our live database immediately (within 30 days if
          you ask us by email instead). Encrypted database backups are overwritten on a rolling basis within a
          further 35 days.
        </li>
        <li>Server logs: only as long as needed for security and troubleshooting.</li>
        <li>Anything we must keep by law: for the period the law requires.</li>
      </ul>
    ),
  },
  {
    id: 'delete-your-data',
    title: 'Deleting your account and data',
    body: (
      <>
        <P>
          <strong>In the app:</strong> open your profile (the avatar menu, top right), choose{' '}
          <strong>Delete my account</strong> and type "delete" to confirm. Your account and all of its data (tasks,
          categories, time logs and AI conversations) are deleted immediately and can't be recovered. To stop usage
          limits being reset by re-creating an account, we keep your AI assistant usage records, under the hashed
          form of your email described in section 2 and without any message content, for 30 days after your last use
          of the assistant, then delete them automatically.
        </P>
        <P>
          <strong>By email:</strong> if you can't sign in, email {email} from the email address of your account, with
          the subject "Delete my account". We may ask you to confirm the request, then delete your data within 30 days
          and confirm when it's done.
        </P>
        <P>
          You can delete individual AI conversations yourself at any time in the app. If you signed in with Google or
          Facebook, you can also remove {BRAND.name}'s access from your{' '}
          <Link href="https://myaccount.google.com/connections" target="_blank" rel="noopener">
            Google account connections
          </Link>{' '}
          or your Facebook settings under "Apps and websites". Removing access stops future sign-ins but does not
          delete the data we already hold: email us for that.
        </P>
      </>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your rights',
    body: (
      <>
        <P>Depending on where you live, you have the right to:</P>
        <ul>
          <li>access the personal data we hold about you and get a copy of it;</li>
          <li>correct it (you can edit your name on your profile page at any time);</li>
          <li>have it deleted, yourself in the app or by asking us (see section 8);</li>
          <li>withdraw your consent, which means closing your account;</li>
          <li>object to or restrict certain processing, and receive your data in a portable format;</li>
          <li>nominate someone to exercise your rights if you die or become incapacitated (India).</li>
        </ul>
        <P>
          Email {email} to use any of these rights. We will respond within 30 days. If you're not satisfied, you can
          contact our Grievance Officer (section 14) and then complain to the Data Protection Board of India or the
          data protection authority where you live.
        </P>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Security',
    body: (
      <P>
        We protect your data with encrypted connections (HTTPS), hashed passwords, a database on a private network
        that is encrypted at rest, and strict per-user separation so no one else can see your content. No system is
        perfectly secure, so we can't guarantee absolute security. If a breach affects your personal data, we will
        notify you and the authorities as the law requires.
      </P>
    ),
  },
  {
    id: 'international',
    title: 'International transfers',
    body: (
      <P>
        Your data is stored and processed in {LEGAL.hostingRegion}. If you use {BRAND.name} from outside India, your
        data is transferred to India. Google and Meta may process sign-in data in other countries under their own
        safeguards.
      </P>
    ),
  },
  {
    id: 'children',
    title: 'Children',
    body: (
      <>
        <P>{BRAND.name} has no adult content and may be used by young people, on these conditions:</P>
        <ul>
          <li>
            <strong>Under {LEGAL.minimumAge}:</strong> not permitted.
          </li>
          <li>
            <strong>
              {LEGAL.minimumAge} to {LEGAL.adultAge - 1}:
            </strong>{' '}
            only with the consent of a parent or legal guardian, who accepts this policy and our Terms on the young
            person's behalf and should supervise their use.
          </li>
        </ul>
        <P>
          We collect no more data from young users than from anyone else, and we never use their data for tracking,
          behavioural monitoring or targeted advertising (we don't do these for anyone). A parent or guardian can
          access, correct or delete their child's data by emailing {email}, or delete the account in the app. If we
          learn that an account belongs to someone under {LEGAL.minimumAge}, or to a minor without a parent's or
          guardian's consent, we will delete it.
        </P>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    body: (
      <P>
        If we change this policy, we'll update the date at the top. For significant changes, we'll tell you in the
        app or by email before they take effect.
      </P>
    ),
  },
  {
    id: 'contact',
    title: 'Contact and Grievance Officer',
    body: (
      <P>
        {LEGAL.operatorName}, {LEGAL.postalAddress}. Email: {email}. Grievance Officer: {LEGAL.grievanceOfficer},
        reachable at the same email address. See also our{' '}
        <Link component={RouterLink} to="/terms">
          Terms of Service
        </Link>
        .
      </P>
    ),
  },
];

const PrivacyPolicyPage: React.FC = () => (
  <LegalPage
    title="Privacy Policy"
    intro={
      <P>
        This policy explains what personal data {BRAND.name} collects, how we use it, who we share it with, and the
        choices and rights you have. By creating an account or using {BRAND.name}, you agree to this policy.
      </P>
    }
    sections={sections}
  />
);

export default PrivacyPolicyPage;
